/**
 * CoConnect — ProductScanner
 *
 * Scan agro-chemical product barcodes/QR codes to verify against MINADER registry.
 * Detects counterfeit products, banned substances, and matches FODECC vouchers.
 *
 * Used by: Farmers, Field Agents, Admins — all feedback goes to admin MEL + Spatial OS.
 */
import React, { useState, useRef, useCallback } from 'react';
import { apiClient } from '../services/api';
import { Card, SectionHeader, Spinner, Alert, StatusPill, EmptyState } from './ui';

interface ScanResult {
  scan: any;
  result: {
    verification_status: 'verified' | 'suspected_counterfeit' | 'banned_substance' | 'not_found' | 'pending';
    matched_product?: any;
    is_counterfeit: boolean;
    counterfeit_indicators: string[];
    warnings: string[];
    fodecc_voucher?: any;
  };
}

const ProductScanner: React.FC<{ farmerId?: string; compact?: boolean }> = ({ farmerId, compact = false }) => {
  const [scanType, setScanType] = useState<'barcode' | 'manual'>('manual');
  const [scannedValue, setScannedValue] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [village, setVillage] = useState('');
  const [region, setRegion] = useState('');
  const [price, setPrice] = useState('');
  const [shop, setShop] = useState('');
  const [notes, setNotes] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleScan = useCallback(async () => {
    if (scanType === 'manual' && !tradeName && !scannedValue) {
      setError('Enter a product name or barcode to scan');
      return;
    }
    setScanning(true);
    setError(null);
    setResult(null);
    try {
      // Get GPS if available
      let lat: number | undefined, lng: number | undefined;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch {}
      }

      const res = await apiClient.post('/agro-products/scan', {
        scan_type: scanType,
        scanned_value: scannedValue || undefined,
        scanned_trade_name: tradeName || undefined,
        scan_location_lat: lat,
        scan_location_lng: lng,
        scan_village: village || undefined,
        scan_region: region || undefined,
        farmer_reported_price: price ? parseInt(price, 10) : undefined,
        farmer_reported_shop: shop || undefined,
        notes: notes || undefined,
        farmer_id: farmerId,
      });
      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [scanType, scannedValue, tradeName, village, region, price, shop, notes, farmerId]);

  const statusTone: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
    verified: 'success',
    pending: 'info',
    suspected_counterfeit: 'warning',
    banned_substance: 'danger',
    not_found: 'danger',
  };

  const statusLabel: Record<string, string> = {
    verified: 'Verified — Genuine MINADER-registered product',
    pending: 'Pending — Product found but not yet MINADER-verified',
    suspected_counterfeit: 'Suspected counterfeit — Barcode does not match registry',
    banned_substance: 'BANNED — Contains banned active ingredient',
    not_found: 'Not found — Product not in MINADER/DRCQ registry',
  };

  return (
    <div className="product-scanner-root farmer-product-scanner-root space-y-4">
      {!compact && (
        <Card padding="lg" className="farmer-product-scanner-intro-card">
          <SectionHeader
            eyebrow="MINADER/DRCQ Registry"
            title="Product Scanner"
            subtitle="Scan or search agro-chemical products to verify authenticity, check for banned substances, and find FODECC subsidy vouchers."
          />
        </Card>
      )}

      <Card padding="lg" className="farmer-product-scanner-form-card">
        {/* Scan type toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => { setScanType('manual'); setScannedValue(''); }}
            className={`text-xs px-3 py-2 rounded-lg font-medium ${scanType === 'manual' ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900' : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300'}`}
          >
            Search by name
          </button>
          <button
            onClick={() => { setScanType('barcode'); setTradeName(''); }}
            className={`text-xs px-3 py-2 rounded-lg font-medium ${scanType === 'barcode' ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900' : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300'}`}
          >
            Barcode / QR
          </button>
        </div>

        {/* Input fields */}
        <div className="space-y-3">
          {scanType === 'barcode' ? (
            <div>
              <label className="label">Barcode or QR code value</label>
              <input
                className="input"
                value={scannedValue}
                onChange={e => setScannedValue(e.target.value)}
                placeholder="e.g. 6001234567890 (EAN-13) or QR content"
              />
              <p className="mt-1 text-2xs text-ink-500 dark:text-ink-400">
                Scan the product's barcode using your phone's camera app, then paste the number here.
                Or type the product name below as a fallback.
              </p>
              <input
                className="input mt-2"
                value={tradeName}
                onChange={e => setTradeName(e.target.value)}
                placeholder="Product name on the box (e.g. Ridomil Gold Plus)"
              />
            </div>
          ) : (
            <div>
              <label className="label">Product name on the box</label>
              <input
                className="input"
                value={tradeName}
                onChange={e => setTradeName(e.target.value)}
                placeholder="e.g. Ridomil Gold Plus 66 WP"
                list="product-names"
              />
              <datalist id="product-names">
                <option value="Ridomil Gold Plus 66 WP" />
                <option value="Nordox 75 WG" />
                <option value="Metachamp" />
                <option value="Orvego" />
                <option value="Callomil Plus" />
                <option value="Callifan Super" />
                <option value="Confidor" />
                <option value="Akate Master" />
                <option value="Bifenthrin" />
                <option value="Aceta Star 46 EC" />
              </datalist>
            </div>
          )}

          {/* Location + price (optional) */}
          <div className="grid grid-cols-2 gap-2">
            <input className="input" value={village} onChange={e => setVillage(e.target.value)} placeholder="Village (optional)" />
            <input className="input" value={region} onChange={e => setRegion(e.target.value)} placeholder="Region (optional)" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input className="input" type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="Price paid (XAF, optional)" />
            <input className="input" value={shop} onChange={e => setShop(e.target.value)} placeholder="Shop name (optional)" />
          </div>
          <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" />

          {error && <Alert tone="danger">{error}</Alert>}

          <button onClick={handleScan} disabled={scanning} className="btn-primary w-full py-3">
            {scanning ? <><Spinner size="sm" /> Verifying...</> : 'Verify product'}
          </button>
        </div>
      </Card>

      {/* Scan result */}
      {result && (
        <Card padding="lg" className="farmer-product-scanner-result-card">
          <div className="mb-4">
            <StatusPill tone={statusTone[result.result.verification_status]} dot>
              {statusLabel[result.result.verification_status]}
            </StatusPill>
          </div>

          {/* Matched product details */}
          {result.result.matched_product && (
            <div className="space-y-2">
              <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{result.result.matched_product.trade_name}</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-ink-500">Type:</span> <span className="font-medium">{result.result.matched_product.product_type}</span></div>
                <div><span className="text-ink-500">Active ingredients:</span> <span className="font-medium">{result.result.matched_product.active_ingredients}</span></div>
                <div><span className="text-ink-500">FRAC/IRAC:</span> <span className="font-medium">{result.result.matched_product.frac_irac_code || '—'}</span></div>
                <div><span className="text-ink-500">MINADER ID:</span> <span className="font-medium">{result.result.matched_product.minader_homologation_id || 'Not registered'}</span></div>
                <div><span className="text-ink-500">Dosage:</span> <span className="font-medium">{result.result.matched_product.dosage_per_15l || result.result.matched_product.dosage_per_ha || '—'}</span></div>
                <div><span className="text-ink-500">Pre-harvest interval:</span> <span className="font-medium">{result.result.matched_product.pre_harvest_interval_days} days</span></div>
                <div><span className="text-ink-500">Bee toxicity:</span> <span className="font-medium capitalize">{result.result.matched_product.bee_toxicity}</span></div>
                <div><span className="text-ink-500">Rain fastness:</span> <span className="font-medium capitalize">{result.result.matched_product.rain_fastness}</span></div>
                <div><span className="text-ink-500">Max applications/season:</span> <span className="font-medium">{result.result.matched_product.max_applications_per_season}</span></div>
                <div><span className="text-ink-500">Avg price:</span> <span className="font-medium nums">{result.result.matched_product.average_price_xaf ? `${Number(result.result.matched_product.average_price_xaf).toLocaleString()} XAF` : '—'}</span></div>
              </div>
              {result.result.matched_product.target_pests && (
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  Targets: {result.result.matched_product.target_pests.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Warnings */}
          {result.result.warnings.length > 0 && (
            <div className="mt-4 space-y-2">
              {result.result.warnings.map((w, i) => (
                <Alert key={i} tone={w.includes('BANNED') || w.includes('counterfeit') || w.includes('not found') ? 'danger' : w.includes('FODECC') || w.includes('✅') ? 'success' : 'warning'}>
                  {w}
                </Alert>
              ))}
            </div>
          )}

          {/* FODECC voucher */}
          {result.result.fodecc_voucher && (
            <div className="mt-4 p-3 rounded-lg bg-success-50 dark:bg-success-500/10 border border-success-500/20">
              <p className="text-sm font-semibold text-success-700 dark:text-success-500">FODECC Subsidy Voucher Available</p>
              <p className="text-xs text-ink-600 dark:text-ink-300 mt-1">
                Code: <span className="font-mono font-bold">{result.result.fodecc_voucher.voucher_code}</span>
                <br />
                Subsidy: {result.result.fodecc_voucher.subsidy_percentage}% off at participating retailers
                <br />
                Valid until: {result.result.fodecc_voucher.valid_until || 'No expiry'}
              </p>
            </div>
          )}

          {/* Counterfeit indicators */}
          {result.result.counterfeit_indicators.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-danger-50 dark:bg-danger-500/10 border border-danger-500/20">
              <p className="text-sm font-semibold text-danger-700 dark:text-danger-500">⚠️ Counterfeit Indicators Detected:</p>
              <ul className="mt-1 text-xs text-ink-600 dark:text-ink-300 list-disc list-inside">
                {result.result.counterfeit_indicators.map((ind, i) => (
                  <li key={i}>{ind.replace(/_/g, ' ')}</li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-danger-700 dark:text-danger-500 font-medium">
                Do not use this product. Report to your cooperative or DRCQ immediately.
              </p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default ProductScanner;
